/**
 * 全域時區處理模組
 * 負責偵測、儲存瀏覽器時區，並在所有 HTTP 請求中自動附帶時區資訊
 */

const TIMEZONE_STORAGE_KEY = 'user_timezone';
const TIMEZONE_HEADER_NAME = 'X-User-Timezone';

/**
 * Detects the browser timezone, or null when the platform cannot answer.
 *
 * Separate from detectBrowserTimezone because the caller has to tell "the
 * browser says Asia/Taipei" apart from "detection failed". Collapsing both into
 * 'UTC' would make the stored fallback unreachable.
 *
 * @returns {string|null} timezone name, or null when detection is unavailable
 */
function detectTimezoneOrNull() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch (error) {
    console.warn('Failed to detect browser timezone:', error);
    return null;
  }
}

/**
 * 偵測瀏覽器當前時區
 * @returns {string} 時區名稱 (例如: 'Asia/Taipei')
 */
function detectBrowserTimezone() {
  return detectTimezoneOrNull() || 'UTC';
}

/**
 * 從 localStorage 取得儲存的時區
 * @returns {string|null} 儲存的時區或 null
 */
function getStoredTimezone() {
  try {
    return localStorage.getItem(TIMEZONE_STORAGE_KEY);
  } catch (error) {
    console.warn('Failed to read timezone from localStorage:', error);
    return null;
  }
}

/**
 * 將時區儲存到 localStorage
 * @param {string} timezone - 時區名稱
 */
function storeTimezone(timezone) {
  try {
    localStorage.setItem(TIMEZONE_STORAGE_KEY, timezone);
  } catch (error) {
    console.warn('Failed to store timezone to localStorage:', error);
  }
}

/**
 * The timezone to report on this request: whatever the browser says now.
 *
 * Detection first, storage only as a fallback. Storage used to win, which meant
 * the value from a visitor's first ever page load was sent forever — after
 * travel, a move, a system setting change, or on a shared computer. The server
 * sets Time.zone for the whole request from this header, so a stale value
 * shifted every displayed time and every availability comparison by the offset
 * between the two zones.
 *
 * Intl is a synchronous local call, so running it per request costs nothing
 * measurable.
 *
 * @returns {string} 時區名稱
 */
function getCurrentUserTimezone() {
  const detected = detectTimezoneOrNull();

  if (detected) {
    // Only on a real change: an unchanged timezone writing on every request
    // would touch localStorage on every fetch for no reason.
    if (detected !== getStoredTimezone()) {
      storeTimezone(detected);
    }
    return detected;
  }

  return getStoredTimezone() || 'UTC';
}

/**
 * 設定 fetch 的預設 header 包含時區
 */
function setupFetchHeaders() {
  // 儲存原始的 fetch 函數
  const originalFetch = window.fetch;

  // 重寫 fetch 函數，自動添加時區 header
  window.fetch = function(input, init = {}) {
    const timezone = getCurrentUserTimezone();

    // 確保 headers 物件存在
    if (!init.headers) {
      init.headers = {};
    }

    // 如果 headers 是 Headers 物件，轉換為普通物件
    if (init.headers instanceof Headers) {
      const headersObj = {};
      init.headers.forEach((value, key) => {
        headersObj[key] = value;
      });
      init.headers = headersObj;
    }

    // 添加時區 header
    init.headers[TIMEZONE_HEADER_NAME] = timezone;

    return originalFetch.call(this, input, init);
  };
}

/**
 * 設定 Turbo 的預設 header 包含時區 - DISABLED (Turbo removed)
 */
function setupTurboHeaders() {
  // Turbo headers disabled as Turbo is removed
}

/**
 * 設定 XMLHttpRequest 的預設 header 包含時區
 */
function setupXHRHeaders() {
  const originalOpen = XMLHttpRequest.prototype.open;
  const originalSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function(method, url, async, user, password) {
    this._method = method;
    this._url = url;
    return originalOpen.call(this, method, url, async, user, password);
  };

  XMLHttpRequest.prototype.send = function(data) {
    const timezone = getCurrentUserTimezone();

    // 在發送前設定時區 header
    if (!this.getResponseHeader(TIMEZONE_HEADER_NAME)) {
      this.setRequestHeader(TIMEZONE_HEADER_NAME, timezone);
    }

    return originalSend.call(this, data);
  };
}

/**
 * 設定 Rails UJS 的 CSRF token 和時區 header
 */
function setupRailsHeaders() {
  // 為 Rails forms 設定預設 header
  document.addEventListener('ajax:beforeSend', (event) => {
    const timezone = getCurrentUserTimezone();
    const xhr = event.detail[0];
    xhr.setRequestHeader(TIMEZONE_HEADER_NAME, timezone);
  });
}

/**
 * 初始化全域時區處理
 *
 * Note: Timezone is no longer auto-updated to database.
 * This function only detects browser timezone and includes it in HTTP headers.
 * Users must manually set their timezone in profile settings.
 */
function initializeGlobalTimezone() {
  // 確保時區已被偵測和儲存（僅用於提供建議）
  getCurrentUserTimezone();

  // 設定各種 HTTP 請求的預設 header
  // Note: Header is sent for timezone detection, but will not trigger database updates
  setupFetchHeaders();
  setupTurboHeaders();
  setupXHRHeaders();
  setupRailsHeaders();

  console.log(`Global timezone initialized (detection only): ${getCurrentUserTimezone()}`);
}

/**
 * 手動更新時區（供外部調用）
 * @param {string} timezone - 新的時區名稱
 */
function updateTimezone(timezone) {
  if (timezone && timezone !== getStoredTimezone()) {
    storeTimezone(timezone);
    console.log(`Timezone manually updated to: ${timezone}`);
  }
}

/**
 * 更新時區並同步到 server（fire-and-forget）
 * @param {string} timezone - 新的時區名稱
 */
function saveTimezoneToServer(timezone) {
  updateTimezone(timezone);
  const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
  fetch('/api/users/update_timezone', {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfToken
    },
    body: JSON.stringify({ timezone })
  }).catch((err) => {
    console.warn('Failed to save timezone:', err);
  });
}

// 導出函數供外部使用
export {
  initializeGlobalTimezone,
  getCurrentUserTimezone,
  detectBrowserTimezone,
  updateTimezone,
  saveTimezoneToServer,
  TIMEZONE_HEADER_NAME
};